import { ChangeDetectorRef, Component, HostListener, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { HotkeysService } from '@ngneat/hotkeys';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { VgStates } from '../VgStates';
import { VideogularComponent } from '../Videogular.Component';
import { userAgentUtil } from '../../UserAgentUtil';
import { HOTKEYS_AVOID_HIDE_CONTROLS } from '../../hotkeys/VbHotkeysRegistration.Directive';

import styles from './VgControls.module.less';

const PAUSE_AUTO_HIDE_TIMEOUT_MS: number = 3000;
const PLAYING_AUTO_HIDE_TIMEOUT_MS: number = 2000;

/**
 * This component acts as a container and you will need other components to control the media.
 * Inside this directive you can add other components like vg-play-pause-button and vg-scrub-bar.
 *
 * <pre>
 * <videogular>
 *    <vg-media></vg-media>
 *
 *    <vg-controls></vg-controls>
 * </videogular>
 * </pre>
 *
 */
@Component({
	selector: 'vg-controls',
	host: {
		'[class]': 'styles.root + " " + (isSmallSizePlayer ? styles.smallSizePlayer : "") + " " + (isMobile ? styles.mobile : "")'
	},
	template: `
		<div
			[ngClass]="[
				styles.controlsContainer,
				isShowing ? styles.showAnimation : styles.hideAnimation
			]">
			<ng-content></ng-content>
		</div>
	`
})
export class VgControlsComponent implements OnChanges, OnDestroy, OnInit {
	@Input() public currentState: VgStates;
	@Input() public isInitialPlay: boolean;
	@Input() public isSmallSizePlayer: boolean;
	@Input() public disableControls: boolean;
	@Input() public forceVgControlsDisplay: boolean;
	@Output() public showVgControls: EventEmitter<boolean> = new EventEmitter();

	private controlSurfacedSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private hideTimeoutRef: number;
	private cursorInHostElement: boolean;
	private sub: Subscription;

	public readonly styles = styles;
	public isShowing: boolean;
	public isMobile: boolean;
	public controlSurfaced$: Observable<boolean> = this.controlSurfacedSubject$.asObservable();

	constructor(
		private changeRef: ChangeDetectorRef,
		private vgApi: VideogularComponent,
		@Optional() private HotkeysService: HotkeysService
	) {}

	public ngOnInit(): void {
		if (this.disableControls) {
			return;
		}
		const videogularElement = this.vgApi.videogularElement;
		this.isMobile = userAgentUtil.isMobile;

		'mousemove touchstart touchmove'
			.split(' ')
			.forEach(event => videogularElement.addEventListener(event, () => this.showControls()));

		'mouseleave touchend'
			.split(' ')
			.forEach(event => videogularElement.addEventListener(event, this.onMouseout));

		this.HotkeysService?.onShortcut(event => {
			this.showControls(this.isInitialPlay || HOTKEYS_AVOID_HIDE_CONTROLS.includes((event as KeyboardEvent).key));
		});

		this.sub = this.vgApi.vgSeeked.subscribe(() => this.showControls());
	}

	public ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}

	public onMouseout = () => {
		if (this.isInitialPlay) {
			return;
		}
		this.initiateHideControls();
	};

	public ngOnChanges(changes: SimpleChanges): void {
		if (this.disableControls) {
			return;
		}

		if (this.isInitialPlay || changes.currentState && this.currentState !== VgStates.PLAY) {
			this.showControls();
		}
	}

	public get isControlsSurfaced(): boolean {
		return this.isShowing;
	}

	public showControls(stayOpen: boolean = false): void {
		this.updateControlClass(true);
		if (stayOpen || this.isInitialPlay || this.cursorInHostElement || this.forceVgControlsDisplay) {
			clearTimeout(this.hideTimeoutRef);
			return;
		}
		this.initiateHideControls();
	}

	private updateControlClass(isShowing: boolean): void {
		window.setTimeout(() => {
			this.isShowing = isShowing;
			this.showVgControls.emit(isShowing);
			this.controlSurfacedSubject$.next(this.isShowing);
			this.changeRef.detectChanges();
		}, 50);
	}

	public initiateHideControls(): void {
		clearTimeout(this.hideTimeoutRef);
		const timeout = this.currentState === VgStates.PAUSE ? PAUSE_AUTO_HIDE_TIMEOUT_MS : PLAYING_AUTO_HIDE_TIMEOUT_MS
		this.hideTimeoutRef = window.setTimeout(() => this.updateControlClass(false), timeout);
	}

	@HostListener('mouseenter')
	public onHostMouseEnter(): void {
		this.cursorInHostElement = true;
	}

	@HostListener('mousemove')
	public onHostMouseMove(): void {
		this.cursorInHostElement = true;
	}

	@HostListener('mouseleave')
	public onHostMouseLeave(): void {
		this.cursorInHostElement = false;
	}

	@HostListener('mouseout')
	public onHostMouseOut(): void {
		this.cursorInHostElement = false;
	}
}
